/**
 * plaidSync.js — Frontend utility for all Plaid API interactions.
 *
 * Calling the serverless functions requires a valid Supabase session token
 * (from supabase.auth.getSession()) which is sent as "Authorization: Bearer <token>".
 *
 * Sandbox test credentials: username "user_good", password "pass_good"
 */

import { supabase } from './supabase';
import { categorizeTransaction } from './categorize';

// ── Auth header ───────────────────────────────────────────────────────────────
async function getAuthHeader() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('No active session — please sign in again.');
  return `Bearer ${session.access_token}`;
}

async function apiFetch(path, options = {}) {
  const auth = await getAuthHeader();
  const res = await fetch(path, {
    ...options,
    headers: {
      'Authorization': auth,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `API error ${res.status}`);
  return data;
}

// ── API wrappers ──────────────────────────────────────────────────────────────

/** Creates a Plaid Link token to open the Plaid UI. */
export async function createLinkToken() {
  const data = await apiFetch('/api/plaid-create-link-token', { method: 'POST' });
  return data.link_token;
}

/** Exchanges the Plaid public_token for a permanent access_token (server-stored). */
export async function exchangeToken(publicToken, metadata) {
  return apiFetch('/api/plaid-exchange-token', {
    method: 'POST',
    body: JSON.stringify({
      public_token:     publicToken,
      institution_name: metadata?.institution?.name  || '',
      institution_id:   metadata?.institution?.institution_id || '',
    }),
  });
}

/** Fetches live account balances for all linked connections. */
export async function fetchPlaidAccounts() {
  return apiFetch('/api/plaid-accounts', { method: 'GET' });
}

/** Fetches transactions for the last N days across all linked accounts. */
export async function fetchPlaidTransactions(startDate, endDate) {
  const today   = new Date().toISOString().slice(0, 10);
  const d90Ago  = new Date(Date.now() - 90 * 86400000).toISOString().slice(0, 10);
  return apiFetch('/api/plaid-transactions', {
    method: 'POST',
    body: JSON.stringify({ start_date: startDate || d90Ago, end_date: endDate || today }),
  });
}

/** Fetches investment holdings across all linked accounts. */
export async function fetchPlaidInvestments() {
  return apiFetch('/api/plaid-investments', { method: 'GET' });
}

/** Removes a Plaid connection (calls Plaid + deletes from DB). */
export async function unlinkPlaidItem(itemId) {
  return apiFetch('/api/plaid-unlink', {
    method: 'POST',
    body: JSON.stringify({ item_id: itemId }),
  });
}

// ── Plaid → app data transformers ────────────────────────────────────────────

/** Maps a Plaid account type+subtype to our internal account type string. */
export function mapPlaidAccountType(type, subtype) {
  if (type === 'depository') {
    if (['savings', 'hsa', 'money market'].includes(subtype)) return 'savings';
    return 'checking';
  }
  if (type === 'credit')  return 'credit_card';
  if (type === 'loan')    return 'mortgage';
  if (type === 'investment') {
    if (subtype === '401k')        return '401k';
    if (subtype === 'roth')        return 'roth_ira';
    if (subtype === 'ira')         return 'ira';
    if (subtype === '403b')        return '401k';
    return 'brokerage';
  }
  return 'other';
}

/** Converts Plaid accounts API response → linkedAccounts format. */
export function buildLinkedAccounts(plaidAccounts, existingLinked = []) {
  const existingIds = new Set(existingLinked.map(a => a.plaidAccountId).filter(Boolean));
  const newAccounts = plaidAccounts
    .filter(acc => !existingIds.has(acc.account_id))
    .map(acc => ({
      id:              `plaid_${acc.account_id}`,
      plaidAccountId:  acc.account_id,
      plaidItemId:     acc.item_id,
      name:            acc.mask ? `${acc.name} (···${acc.mask})` : acc.name,
      institution:     acc.institution_name,
      type:            mapPlaidAccountType(acc.type, acc.subtype),
      balance:         acc.balance_current ?? acc.balance_available ?? 0,
      connectionType:  'plaid',
      lastUpdated:     new Date().toISOString(),
      manualBannerDismissed: true,
    }));

  // Update balances for already-linked Plaid accounts
  const updated = existingLinked.map(acc => {
    if (acc.connectionType !== 'plaid') return acc;
    const live = plaidAccounts.find(p => p.account_id === acc.plaidAccountId);
    if (!live) return acc;
    return {
      ...acc,
      balance:     live.balance_current ?? live.balance_available ?? acc.balance,
      lastUpdated: new Date().toISOString(),
    };
  });

  return [...updated, ...newAccounts];
}

// ── Plaid primary category → standard budget category mapping ─────────────────
// Candidates are listed in preference order. The first candidate that matches
// a user category name (via substring) wins. Names here must match
// STANDARD_CATEGORIES in Budget.js so keyword matching always finds a row.
const PLAID_CATEGORY_MAP = {
  FOOD_AND_DRINK:            ['Dining Out', 'Groceries', 'Shopping'],
  TRANSPORTATION:            ['Transportation', 'Insurance'],
  TRAVEL:                    ['Travel', 'Entertainment'],
  GENERAL_MERCHANDISE:       ['Shopping', 'Hobbies'],
  PERSONAL_CARE:             ['Personal Care', 'Shopping'],
  MEDICAL:                   ['Healthcare', 'Insurance'],
  ENTERTAINMENT:             ['Entertainment', 'Subscriptions', 'Fitness', 'Hobbies'],
  ENTERTAINMENT_SUBSCRIPTIONS: ['Subscriptions', 'Entertainment'],
  RENT_AND_UTILITIES:        ['Housing', 'Utilities'],
  LOAN_PAYMENTS:             ['Minimum Debt Payments', 'Extra Debt Paydown', 'Housing'],
  HOME_IMPROVEMENT:          ['Housing', 'Shopping'],
  GOVERNMENT_AND_NON_PROFIT: ['Hobbies', 'Shopping'],
  EDUCATION:                 ['Education Fund', 'Hobbies'],
  BANK_FEES:                 ['Shopping'],
  GENERAL_SERVICES:          ['Shopping', 'Subscriptions'],
  INCOME:                    null,   // skip — salary/payroll credits
  TRANSFER_IN:               null,   // skip — money arriving (deposit, refund)
  // TRANSFER_OUT intentionally omitted: ACH rent, loan, and credit-card payments
  // are TRANSFER_OUT in Plaid but are real expenses. Let keyword matching handle them.
};

/**
 * Finds the best matching budget category name for a Plaid category code.
 * First tries exact standard names, then substring match against user categories.
 * Falls back to 'Shopping' (most common catch-all) if nothing matches.
 */
function matchBudgetCategory(plaidCategory, userCategories) {
  const candidates = PLAID_CATEGORY_MAP[plaidCategory];
  if (candidates === null) return null;  // income/transfer — skip
  if (!candidates) return 'Shopping';

  const userNames = userCategories.map(c => c.name);
  const userNamesLower = userNames.map(n => n.toLowerCase());

  for (const candidate of candidates) {
    // Exact match first
    const exact = userNames.find(n => n === candidate);
    if (exact) return exact;
    // Substring match
    const sub = userNamesLower.find(n =>
      n.includes(candidate.toLowerCase()) || candidate.toLowerCase().includes(n)
    );
    if (sub) return userNames[userNamesLower.indexOf(sub)];
  }

  // Fall back to first non-savings category, otherwise first category
  const firstExpense = userCategories.find(c => c.type !== 'Savings');
  return firstExpense?.name || userCategories[0]?.name || 'Shopping';
}

/**
 * Converts Plaid transactions → budget transactions format.
 * Skips income / transfer / pending transactions.
 * Auto-categorizes using the user's existing budget categories.
 */
export function buildBudgetTransactions(plaidTransactions, userCategories, existingTransactions = []) {
  const existingIds = new Set(existingTransactions.map(t => t.plaidTxnId).filter(Boolean));

  const newTxns = plaidTransactions
    .filter(txn => {
      if (txn.pending)  return false;  // skip pending
      if (txn.amount <= 0) return false; // skip credits/income (negative Plaid amount = credit)
      const skip = PLAID_CATEGORY_MAP[txn.category] === null;
      if (skip) return false;
      return !existingIds.has(txn.transaction_id);
    })
    .map(txn => {
      const desc = txn.merchant_name || txn.name || '';
      // 1. Keyword-based matching — highest confidence, maps to standard names
      const kwMatch = categorizeTransaction(desc, {});
      // 2. Plaid category code → standard category fallback
      const fallbackCat  = kwMatch ? null : matchBudgetCategory(txn.category, userCategories);
      const categoryName = kwMatch?.category || fallbackCat || null;
      // Derive type: keyword match first, then look up from user's category list
      const matchedCat   = categoryName ? userCategories.find(c => c.name === categoryName) : null;
      const txnType      = kwMatch?.type || matchedCat?.type || null;
      return {
        id:            `plaid_${txn.transaction_id}`,
        plaidTxnId:    txn.transaction_id,
        plaidCategory: txn.category || null,
        date:          txn.date,
        amount:        Math.abs(txn.amount),
        description:   desc,
        category:      categoryName,
        type:          txnType,
        kind:          'expense',
        source:        kwMatch ? 'auto' : (fallbackCat ? 'auto' : 'plaid'),
        reviewed:      !!categoryName,   // auto-reviewed when category was determined; false = needs user review
      };
    });

  return [...existingTransactions, ...newTxns];
}

/**
 * Converts Plaid investment holdings → Investing module format.
 * Maps security types to asset classes.
 */
function mapAssetClass(secType, ticker = '') {
  const t = (ticker || '').toUpperCase();
  const s = (secType || '').toLowerCase();

  // Common index/ETF tickers → asset class
  if (['SPY','IVV','VOO','VTI','FXAIX','SWTSX'].some(x => t.includes(x))) return 'Large Cap US';
  if (['VXUS','EFA','EEM','IEMG','VEA','VWO'].some(x => t.includes(x))) return 'International';
  if (['VNQ','REET','SCHH'].some(x => t.includes(x))) return 'REITs';
  if (['BND','AGG','TLT','IEF','LQD'].some(x => t.includes(x))) return 'Bonds';
  if (['VB','IJR','IWM','VXF'].some(x => t.includes(x))) return 'Mid/Small Cap';
  if (s.includes('mutual fund') || s.includes('etf')) return 'Large Cap US';
  if (s.includes('bond') || s.includes('fixed')) return 'Bonds';
  if (s.includes('cash') || s.includes('money')) return 'Cash';
  return 'Large Cap US';
}

export function buildInvestingData(plaidHoldings, existingInvesting) {
  if (!plaidHoldings?.length) return existingInvesting;

  // Group holdings by account
  const accountMap = {};
  for (const h of plaidHoldings) {
    if (!accountMap[h.account_id]) {
      accountMap[h.account_id] = {
        id:       `plaid_${h.account_id}`,
        name:     h.account_name,
        type:     mapPlaidAccountType('investment', h.account_subtype || 'brokerage'),
        holdings: [],
      };
    }
    accountMap[h.account_id].holdings.push({
      ticker:    h.ticker,
      name:      h.name,
      shares:    h.shares,
      value:     h.value || 0,
      costBasis: h.cost_basis ? h.cost_basis / (h.shares || 1) : 0,
      assetClass: mapAssetClass(h.type, h.ticker),
    });
  }

  const linkedAccounts = Object.values(accountMap);
  const totalValue = linkedAccounts.reduce(
    (sum, acc) => sum + acc.holdings.reduce((s, h) => s + h.value, 0), 0
  );
  const totalCost = plaidHoldings.reduce((sum, h) => sum + (h.cost_basis || 0), 0);
  const totalReturn = totalValue - totalCost;
  const totalReturnPct = totalCost > 0 ? (totalReturn / totalCost) * 100 : 0;

  return {
    ...(existingInvesting || {}),
    linkedAccounts,
    totalValue,
    totalReturn,
    totalReturnPct,
    dayChange:    0,    // Plaid doesn't provide intraday change
    dayChangePct: 0,
    lastSynced:   new Date().toISOString(),
    availableProviders: [],
    sectorExposure: {},
  };
}

/**
 * Builds Net Worth assets/liabilities from Plaid accounts.
 */
export function buildNetWorthFromAccounts(plaidAccounts, existingNW) {
  const assetTypes = ['depository', 'investment'];
  const liabilityTypes = ['credit', 'loan'];

  const plaidAssetNames = new Set();
  const plaidLiabNames  = new Set();

  const newAssets = plaidAccounts
    .filter(acc => assetTypes.includes(acc.type))
    .map(acc => {
      const name = `${acc.institution_name} ${acc.name}`;
      plaidAssetNames.add(name);
      const nwType = acc.type === 'investment'
        ? (acc.subtype?.includes('401') || acc.subtype?.includes('ira') ? 'Retirement' : 'Investment')
        : 'Cash';
      return { name, type: nwType, value: acc.balance_current ?? acc.balance_available ?? 0 };
    });

  const newLiabilities = plaidAccounts
    .filter(acc => liabilityTypes.includes(acc.type))
    .map(acc => {
      const name = `${acc.institution_name} ${acc.name}`;
      plaidLiabNames.add(name);
      return { name, type: acc.subtype || acc.type, value: Math.abs(acc.balance_current ?? 0) };
    });

  // Keep existing manual entries, replace Plaid-sourced ones
  const existingAssets = (existingNW?.assets || [])
    .filter(a => !plaidAssetNames.has(a.name));
  const existingLiabs  = (existingNW?.liabilities || [])
    .filter(l => !plaidLiabNames.has(l.name));

  return {
    ...(existingNW || {}),
    assets:      [...existingAssets, ...newAssets],
    liabilities: [...existingLiabs,  ...newLiabilities],
  };
}

/**
 * Master sync — call after successful Plaid link or on manual refresh.
 * Fetches all Plaid data and returns updated section payloads.
 * The caller is responsible for saving via updateSection().
 */
export async function syncAllPlaidData({ budgetData, netWorthData, investingData }) {
  const [accountsRes, transactionsRes, investmentsRes] = await Promise.allSettled([
    fetchPlaidAccounts(),
    fetchPlaidTransactions(),
    fetchPlaidInvestments(),
  ]);

  const result = {
    accounts:     [],
    newNetWorth:  netWorthData,
    newBudget:    budgetData,
    newInvesting: investingData,
    errors:       [],
  };

  if (accountsRes.status === 'fulfilled') {
    result.accounts = accountsRes.value.accounts || [];
    result.newNetWorth = buildNetWorthFromAccounts(result.accounts, netWorthData);
  } else {
    result.errors.push(`Accounts: ${accountsRes.reason?.message}`);
  }

  if (transactionsRes.status === 'fulfilled') {
    const txns = transactionsRes.value.transactions || [];
    const updatedTxns = buildBudgetTransactions(
      txns,
      budgetData?.categories || [],
      budgetData?.transactions || []
    );
    result.newBudget = { ...budgetData, transactions: updatedTxns };
  } else {
    result.errors.push(`Transactions: ${transactionsRes.reason?.message}`);
  }

  if (investmentsRes.status === 'fulfilled') {
    const { holdings } = investmentsRes.value;
    if (holdings?.length) {
      result.newInvesting = buildInvestingData(holdings, investingData);
    }
  } else {
    result.errors.push(`Investments: ${investmentsRes.reason?.message}`);
  }

  return result;
}
